import {trackEvent} from 'analytics/Analytics';
import {assertAndContinue} from 'Asserts';
import {ELN_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import FileStates from 'FileStates';
import NoteTabState from 'notebook/notes/tabs/NoteTabState';
import ReviewTabState from 'notebook/reviews/ReviewTabState';

/**
 * Rearrange the tab state to show the AI checker system.
 *
 * This should always be called on an entry ID that is already open, and should only be called
 * when the review feature is enabled. (Currently, this means we should only enable AI notebook
 * checking when reviews are enabled.)
 *
 * Because the review tab loads AI notebook checks automatically when open, this function simply
 * rearranges the tabs by splitting workspace and putting the note view on the left and review
 * on the right.
 */
export default function openAICheckerPanel(fileId: string, origin: string): void {
  const fileState = FileStates.get().get(fileId);
  if (fileState == null) {
    assertAndContinue(false, `Unexpected missing filestate for ${fileId}.`);
    return;
  }
  fileState.setIsWorkspaceSplit(true);
  const noteTab = fileState.findTab(NoteTabState);
  if (noteTab == null) {
    assertAndContinue(false, 'Unexpected missing note tab.');
    return;
  }
  const reviewTab = fileState.findTab(ReviewTabState);
  if (reviewTab == null) {
    assertAndContinue(false, 'Unexpected missing review tab.');
    return;
  }
  const leftBuffer = fileState.buffers().at(0);
  const rightBuffer = fileState.buffers().at(1);
  if (rightBuffer.tabs().contains(noteTab)) {
    fileState.buffers().moveTab(noteTab, leftBuffer, 0);
  }
  if (leftBuffer.tabs().contains(reviewTab)) {
    fileState.buffers().moveTab(reviewTab, rightBuffer, 0);
  }
  if (!noteTab.get('active')) {
    noteTab.set('active', true);
  }
  if (!reviewTab.get('active')) {
    reviewTab.set('active', true);
  }
  trackEvent(ELN_ANALYTICS_ACTIONS.OPENED_AI_NOTEBOOK_CHECKER, {origin});
}
